import { useState, useEffect, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Clock, PoundSterling, Building, FileText, CreditCard } from "lucide-react";
import { ActSelector } from "@/components/acts/act-selector";
import { FeeComparisonIndicator } from "@/components/bookings/fee-comparison-indicator";
import type { Venue, ActWithParticipants } from "@shared/schema";

interface VenueBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organisationId: number;
  venueId?: number;
  actId?: number;
}

export function VenueBookingModal({
  open,
  onOpenChange,
  organisationId,
  venueId,
  actId,
}: VenueBookingModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedAct, setSelectedAct] = useState<ActWithParticipants | null>(null);

  const [formData, setFormData] = useState({
    venueId: venueId || 0,
    actId: actId || 0,
    performanceTime: "",
    durationMinutes: "",
    status: "pending" as "pending" | "confirmed" | "cancelled" | "completed",
    fee: "",
    commissionType: "" as "" | "percentage" | "flat",
    commissionValue: "",
    addCommissionToFee: false,
    hasDeposit: false,
    depositAmount: "",
    depositPaid: false,
    contractSigned: false,
    paymentStatus: "pending" as "pending" | "paid" | "partial" | "refunded",
    invoiceNumber: "",
    notes: "",
  });

  // Fetch venues
  const { data: venues = [] } = useQuery<Venue[]>({
    queryKey: [`/api/venues?organisationId=${organisationId}`],
    queryFn: async () => {
      const response = await fetch(`/api/venues?organisationId=${organisationId}`);
      if (!response.ok) throw new Error("Failed to fetch venues");
      return response.json();
    },
    enabled: !!organisationId,
  });

  // Generate event name from act name
  const eventName = useMemo(() => {
    if (selectedAct) {
      const actName = selectedAct.name || selectedAct.participants?.[0]?.participant.name || "Unknown Act";
      return `${actName} Live Performance`;
    }
    return "";
  }, [selectedAct]);

  // Set default venue if provided
  useEffect(() => {
    if (venueId && venues.length > 0) {
      setFormData(prev => ({ ...prev, venueId }));
    }
  }, [venueId, venues]);

  // Handle act selection
  const handleActSelection = (act: ActWithParticipants | null) => {
    setSelectedAct(act);
    setFormData(prev => ({ ...prev, actId: act?.id || 0 }));
  };

  // Get selected venue details
  const selectedVenue = useMemo(() => 
    venues.find(v => v.id === formData.venueId),
    [venues, formData.venueId]
  );

  // Create venue booking mutation
  const mutation = useMutation({
    mutationFn: async (data: any) => {
      const response = await fetch(`/api/bookings/venue?organisationId=${organisationId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to create venue booking");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [`/api/bookings`] });
      queryClient.invalidateQueries({ queryKey: [`/api/external-events`] });
      toast({
        title: "Booking Created",
        description: "The venue booking and event have been created successfully.",
      });
      onOpenChange(false);
      // Reset form
      setFormData({
        venueId: 0,
        actId: 0,
        performanceTime: "",
        durationMinutes: "",
        status: "pending",
        fee: "",
        commissionType: "",
        commissionValue: "",
        addCommissionToFee: false,
        hasDeposit: false,
        depositAmount: "",
        depositPaid: false,
        contractSigned: false,
        paymentStatus: "pending",
        invoiceNumber: "",
        notes: "",
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.venueId || !formData.actId || !formData.performanceTime) {
      toast({
        title: "Missing Required Fields",
        description: "Please select a venue, act, and performance time.",
        variant: "destructive",
      });
      return;
    }

    // Calculate the final fee including commission if "Add to Fee" is checked
    let finalFee = formData.fee ? parseFloat(formData.fee) : 0;
    if (formData.commissionType === "flat" && formData.addCommissionToFee && formData.commissionValue) {
      finalFee += parseFloat(formData.commissionValue);
    }

    const data: any = {
      venueId: formData.venueId,
      actId: formData.actId,
      eventName: eventName,
      eventDate: new Date(formData.performanceTime).toISOString(), // Use performance time as event date
      useVenueContact: true, // Always use venue contact for streamlined flow
      booking: {
        status: formData.status,
        fee: finalFee ? Math.round(finalFee * 100) : undefined,
        commissionType: formData.commissionType || undefined,
        commissionValue: formData.commissionValue && formData.commissionType
          ? formData.commissionType === "percentage"
            ? Math.round(parseFloat(formData.commissionValue) * 100) // Convert to basis points
            : Math.round(parseFloat(formData.commissionValue) * 100) // Convert to cents
          : undefined,
        hasDeposit: formData.hasDeposit,
        depositAmount: formData.hasDeposit && formData.depositAmount 
          ? Math.round(parseFloat(formData.depositAmount) * 100) // Convert to cents
          : undefined,
        depositPaid: formData.depositPaid,
        performanceTime: new Date(formData.performanceTime).toISOString(),
        durationMinutes: formData.durationMinutes ? parseInt(formData.durationMinutes) : undefined,
        contractSigned: formData.contractSigned,
        paymentStatus: formData.paymentStatus,
        invoiceNumber: formData.invoiceNumber || undefined,
        notes: formData.notes || undefined,
      }
    };

    mutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building className="h-5 w-5 text-blue-600" />
            Create Venue Booking
          </DialogTitle>
          <DialogDescription>
            Quick booking at a venue - automatically creates an event named after your act.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Venue and Act Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="venue">
                <Building className="inline h-4 w-4 mr-1" />
                Venue *
              </Label>
              <Select
                value={formData.venueId.toString()}
                onValueChange={(value) => setFormData(prev => ({ ...prev, venueId: parseInt(value) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select venue" />
                </SelectTrigger>
                <SelectContent>
                  {venues.map((venue) => (
                    <SelectItem key={venue.id} value={venue.id.toString()}>
                      {venue.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <ActSelector
                organisationId={organisationId}
                selectedActId={actId || formData.actId}
                onSelectAct={handleActSelection}
                label="Act"
                placeholder="Search for acts..."
                required={true}
              />
            </div>
          </div>

          {/* Auto-generated Event Info */}
          {selectedAct && selectedVenue && (
            <div className="rounded-lg bg-blue-50 border border-blue-200 p-3">
              <p className="text-sm text-blue-800">
                <strong>Event will be created:</strong> "{eventName}" at {selectedVenue.name}
              </p>
            </div>
          )}

          {/* Booking Details */}
          <div className="space-y-4 rounded-lg border border-slate-200 p-4">
            <h3 className="font-medium text-sm text-slate-700">Booking Details</h3>
            
            {/* Performance DateTime and Duration */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="performanceTime">
                  <Clock className="inline h-4 w-4 mr-1" />
                  Performance Date & Time *
                </Label>
                <Input
                  id="performanceTime"
                  type="datetime-local"
                  value={formData.performanceTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, performanceTime: e.target.value }))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="duration">
                  <Clock className="inline h-4 w-4 mr-1" />
                  Duration (minutes)
                </Label>
                <Input
                  id="duration"
                  type="number"
                  value={formData.durationMinutes}
                  onChange={(e) => setFormData(prev => ({ ...prev, durationMinutes: e.target.value }))}
                  placeholder="60"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status">Booking Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value: any) => setFormData(prev => ({ ...prev, status: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="confirmed">Confirmed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="fee">
                  <PoundSterling className="inline h-4 w-4 mr-1" />
                  Performance Fee
                </Label>
                <Input
                  id="fee"
                  type="number"
                  step="0.01"
                  value={formData.fee}
                  onChange={(e) => setFormData(prev => ({ ...prev, fee: e.target.value }))}
                  placeholder="1500.00"
                />
                <FeeComparisonIndicator
                  actualFee={formData.fee ? parseFloat(formData.fee) * 100 : null}
                  minimumFee={selectedAct?.minimumFee}
                  preferredFee={selectedAct?.preferredFee}
                />
              </div>
            </div>

            {/* Payment Status and Invoice */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="paymentStatus">
                  <CreditCard className="inline h-4 w-4 mr-1" />
                  Payment Status
                </Label>
                <Select
                  value={formData.paymentStatus}
                  onValueChange={(value: any) => setFormData(prev => ({ ...prev, paymentStatus: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="refunded">Refunded</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="invoiceNumber">
                  <FileText className="inline h-4 w-4 mr-1" />
                  Invoice Number
                </Label>
                <Input
                  id="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={(e) => setFormData(prev => ({ ...prev, invoiceNumber: e.target.value }))}
                  placeholder="INV-2024-001"
                />
              </div>
            </div>

            {/* Deposit Section */}
            <div className="space-y-3 border-t pt-3">
              <div className="flex items-center justify-between">
                <Label htmlFor="hasDeposit" className="text-base font-medium">
                  Deposit Required
                </Label>
                <Switch
                  id="hasDeposit"
                  checked={formData.hasDeposit}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, hasDeposit: checked }))}
                />
              </div>
              
              {formData.hasDeposit && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ml-4">
                  <div>
                    <Label htmlFor="depositAmount">
                      <PoundSterling className="inline h-4 w-4 mr-1" />
                      Deposit Amount
                    </Label>
                    <Input
                      id="depositAmount"
                      type="number"
                      step="0.01"
                      value={formData.depositAmount}
                      onChange={(e) => setFormData(prev => ({ ...prev, depositAmount: e.target.value }))}
                      placeholder="0.00"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      id="depositPaid"
                      checked={formData.depositPaid}
                      onCheckedChange={(checked) => setFormData(prev => ({ ...prev, depositPaid: checked }))}
                    />
                    <Label htmlFor="depositPaid" className="cursor-pointer">
                      Deposit Paid
                    </Label>
                  </div>
                </div>
              )}
            </div>

            {/* Commission Details */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="commissionType">Commission Type</Label>
                <Select
                  value={formData.commissionType}
                  onValueChange={(value: any) => setFormData(prev => ({ ...prev, commissionType: value, addCommissionToFee: false }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select commission type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percentage">Percentage of Fee</SelectItem>
                    <SelectItem value="flat">Flat Amount</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="commissionValue">
                  Commission {formData.commissionType === "percentage" ? "(%)" : "(£)"}
                </Label>
                <Input
                  id="commissionValue"
                  type="number"
                  step={formData.commissionType === "percentage" ? "0.01" : "1"}
                  value={formData.commissionValue}
                  onChange={(e) => setFormData(prev => ({ ...prev, commissionValue: e.target.value }))}
                  placeholder={formData.commissionType === "percentage" ? "10" : "150.00"}
                  disabled={!formData.commissionType}
                />
                {formData.fee && formData.commissionValue && formData.commissionType && (
                  <p className="text-xs text-slate-600 mt-1">
                    Commission: £{
                      formData.commissionType === "percentage"
                        ? ((parseFloat(formData.fee) * parseFloat(formData.commissionValue)) / 100).toFixed(2)
                        : parseFloat(formData.commissionValue).toFixed(2)
                    }
                    {formData.commissionType === "percentage" && 
                      ` | Net: £${(parseFloat(formData.fee) - (parseFloat(formData.fee) * parseFloat(formData.commissionValue)) / 100).toFixed(2)}`
                    }
                    {formData.commissionType === "flat" && !formData.addCommissionToFee &&
                      ` | Net: £${(parseFloat(formData.fee) - parseFloat(formData.commissionValue)).toFixed(2)}`
                    }
                    {formData.commissionType === "flat" && formData.addCommissionToFee &&
                      ` | Total Fee: £${(parseFloat(formData.fee) + parseFloat(formData.commissionValue)).toFixed(2)}`
                    }
                  </p>
                )}
              </div>
            </div>
            
            {/* Add to Fee Toggle for Flat Commission */}
            {formData.commissionType === "flat" && formData.commissionValue && (
              <div className="flex items-center gap-2 bg-blue-50 rounded-lg p-3 border border-blue-200">
                <input
                  type="checkbox"
                  id="addCommissionToFee"
                  checked={formData.addCommissionToFee}
                  onChange={(e) => setFormData(prev => ({ ...prev, addCommissionToFee: e.target.checked }))}
                  className="h-4 w-4"
                />
                <Label htmlFor="addCommissionToFee" className="cursor-pointer text-sm">
                  Add to Fee (Client pays £{(parseFloat(formData.fee || "0") + parseFloat(formData.commissionValue)).toFixed(2)} total)
                </Label>
              </div>
            )}

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="contractSigned"
                checked={formData.contractSigned}
                onChange={(e) => setFormData(prev => ({ ...prev, contractSigned: e.target.checked }))}
                className="h-4 w-4"
              />
              <Label htmlFor="contractSigned" className="cursor-pointer">
                Contract Signed
              </Label>
            </div>

            <div>
              <Label htmlFor="notes">Booking Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                placeholder="Any additional notes about this booking..."
              />
            </div>
          </div>
        </div>

        <DialogFooter className="flex-col-reverse sm:flex-row gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={mutation.isPending}
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
          >
            {mutation.isPending ? "Creating..." : "Create Venue Booking"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}